import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import FeedbackSection from "@/components/FeedbackSection";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/questions";
import TextSearchModal from "@/components/TextSearchModal";
import ClientCatalogShowcase from "@/components/ClientCatalogShowcase";
import { ExtraPricesButton } from "@/components/AgentPrices";

export default function Landing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [textSearchOpen, setTextSearchOpen] = useState(false);

  return (
    <div className="min-h-screen tm-grid-bg tm-noise">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-800">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwZmFjdG9yeSUyMG1hY2hpbmVyeSUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc4NDI5MDMzM3ww&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.6)",
          }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.85) 60%, rgba(15,23,42,0.6) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 py-12 sm:py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-4 sm:mb-6" data-testid="hero-tag">
              ● Product Finder Industriale
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white leading-none mb-6 sm:mb-8" data-testid="hero-title">
              Il prodotto <span className="text-orange-500">TOP-TEC</span><br />
              giusto per te,<br />
              in 60 secondi.
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 sm:mb-10 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              Rispondi a poche domande tecniche. Il nostro motore incrocia le tue esigenze con l'archivio delle schede tecniche TOP-TEC e ti mostra fino a 3 soluzioni ordinate per idoneità.
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4">
              <a href="#categories" className="tm-btn-primary text-center" data-testid="start-cta">
                Inizia la ricerca →
              </a>
              <button
                type="button"
                onClick={() => setTextSearchOpen(true)}
                className="py-2 text-sm text-slate-300 hover:text-orange-400 underline underline-offset-4 decoration-slate-600 hover:decoration-orange-400 transition-colors font-mono tracking-wide"
                data-testid="text-search-cta"
              >
                oppure cerca per nome o descrizione
              </button>
              {isAdmin && (
                <Link to="/admin" className="tm-btn-outline" data-testid="hero-admin-link">
                  Vai al Dashboard →
                </Link>
              )}
              {user?.role === "agent" && <ExtraPricesButton className="text-center" />}
            </div>
          </div>
        </div>
      </section>

      {textSearchOpen && (
        <TextSearchModal onClose={() => setTextSearchOpen(false)} />
      )}

      {/* CATEGORIES */}
      <section id="categories" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 scroll-mt-24">
        <div className="mb-8 sm:mb-12 flex items-end justify-between flex-wrap gap-4 sm:gap-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-3" data-testid="cat-section-tag">
              [ 01 / Scegli l'operazione ]
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-white tracking-tighter" data-testid="cat-section-title">
              Cosa devi fare?
            </h2>
          </div>
          <p className="text-slate-400 max-w-md">
            Seleziona una categoria per iniziare il questionario guidato.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {Object.values(CATEGORIES).map((cat) => (
            <Link
              key={cat.slug}
              to={`/wizard/${cat.slug}`}
              className="tm-card tm-card-hover group flex sm:block"
              data-testid={`category-card-${cat.slug}`}
            >
              <div className="w-28 shrink-0 sm:w-auto sm:aspect-[4/3] overflow-hidden border-r sm:border-r-0 sm:border-b border-slate-700 relative">
                <img
                  src={cat.image}
                  alt={cat.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute top-2 left-2 sm:top-3 sm:left-3 tm-tag bg-slate-900/80">{cat.code}</div>
              </div>
              <div className="p-4 sm:p-6 flex-1 min-w-0 flex flex-col justify-center">
                <h3 className="font-heading text-xl sm:text-2xl text-white mb-1 sm:mb-2 tracking-tighter">{cat.title}</h3>
                <p className="text-slate-400 text-sm mb-3 sm:mb-6">{cat.subtitle}</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-orange-500 group-hover:text-orange-400">
                    Avvia
                  </span>
                  <span className="text-orange-500 text-xl group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="font-mono text-xs uppercase tracking-widest text-orange-500 mb-3">
            [ 02 / Come funziona ]
          </div>
          <h2 className="font-heading text-3xl md:text-5xl text-white tracking-tighter mb-8 sm:mb-12">
            Un processo lineare.
          </h2>
          <div className="grid md:grid-cols-3 gap-0">
            {[
              { n: "01", t: "Rispondi", d: "Descrivi la lavorazione e rispondi a poche domande tecniche mirate: materiali, temperature, resistenze e vincoli specifici." },
              { n: "02", t: "Confrontiamo", d: "Il sistema incrocia le tue risposte con l'archivio schede tecniche TOP-TEC estratto tramite AI." },
              { n: "03", t: "Ricevi 3 opzioni", d: "Ti mostriamo massimo 3 prodotti compatibili ordinati per grado di corrispondenza." },
            ].map((s, i) => (
              <div key={s.n} className={`p-5 sm:p-8 border-slate-700 ${i < 2 ? 'md:border-r' : ''} border-b md:border-b-0`}>
                <div className="font-mono text-orange-500 text-sm mb-4">{s.n}</div>
                <h3 className="font-heading text-2xl text-white mb-3 tracking-tighter">{s.t}</h3>
                <p className="text-slate-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ClientCatalogShowcase />

      <FeedbackSection />

      <footer className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-8 flex justify-between items-center text-sm text-slate-500 font-mono">
          <span>© 2026 TOP-TEC</span>
          <span className="uppercase tracking-widest text-xs">Made for engineers</span>
        </div>
      </footer>
    </div>
  );
}
